import { useCallback, useEffect, useState } from 'react';
import api, { API_BASE } from '@/lib/api';
import { PageHeader, EmptyState, LoadingShell } from '@/components/ui-bits';
import { CalendarClock, Pause, Play, XCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { fmtRelative } from '@/lib/format';

const STATUS_LABEL = {
  scheduled: 'Programada',
  running: 'En curso',
  paused: 'Pausada',
  completed: 'Completada',
  cancelled: 'Cancelada',
  failed: 'Fallida',
};

const STATUS_CLASS = {
  scheduled: 'bg-[hsl(var(--chart-2))]/15 text-[hsl(var(--chart-2))]',
  running: 'bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]',
  paused: 'bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]',
  completed: 'bg-muted text-muted-foreground',
  cancelled: 'bg-muted text-muted-foreground',
  failed: 'bg-[hsl(var(--danger))]/15 text-[hsl(var(--danger))]',
};

function formatInterval(sec) {
  const n = Number(sec) || 0;
  if (n < 60) return `${n}s`;
  if (n < 3600) return `${Math.round(n / 60)} min`;
  return `${Math.round(n / 3600)} h`;
}

function formatWhen(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-MX', {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

export default function CampaignsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [channel, setChannel] = useState(''); // '' | whatsapp | email
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [stages, setStages] = useState([]);
  const [tags, setTags] = useState([]);
  const [leadLists, setLeadLists] = useState([]);
  const [form, setForm] = useState({
    name: '', channel: 'whatsapp', body: '', subject: '', interval_seconds: 30,
    mode: 'now', scheduledLocal: '', stage_id: '', tag: '', list_id: '', respect_quiet_hours: true,
  });

  const load = useCallback(async () => {
    try {
      const q = channel ? `?limit=100&channel=${channel}` : '?limit=100';
      const { data } = await api.get(`/whatsapp/campaigns${q}`);
      setItems(data.campaigns || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al cargar campañas');
    } finally {
      setLoading(false);
    }
  }, [channel]);

  useEffect(() => {
    load();
    const t = setInterval(load, 8000); // auto-refresh progress
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    api.get('/pipeline').then((r) => setStages((r.data.pipeline?.stages || []).sort((a, b) => a.order - b.order))).catch(() => {});
    api.get('/tags').then((r) => setTags(r.data.tags || [])).catch(() => {});
    api.get('/lead-lists?active_only=true').then((r) => setLeadLists(r.data.lists || [])).catch(() => {});
  }, []);

  const createFromAudience = async () => {
    if (!form.body.trim() && form.channel === 'whatsapp') {
      toast.error('Escribe el mensaje');
      return;
    }
    if (form.channel === 'email' && (!form.subject.trim() || !form.body.trim())) {
      toast.error('Asunto y cuerpo requeridos');
      return;
    }
    setCreating(true);
    try {
      const payload = {
        channel: form.channel,
        body: form.body,
        subject: form.channel === 'email' ? form.subject : undefined,
        interval_seconds: Number(form.interval_seconds) || 30,
        name: form.name || undefined,
        respect_quiet_hours: form.respect_quiet_hours,
        audience: {
          list_id: form.list_id || undefined,
          stage_id: form.list_id ? undefined : (form.stage_id || undefined),
          tag: form.list_id ? undefined : (form.tag || undefined),
          has_phone: form.channel === 'whatsapp',
          has_email: form.channel === 'email',
          status: form.list_id ? undefined : 'open',
        },
        lead_ids: [],
      };
      if (form.mode === 'schedule' && form.scheduledLocal) {
        payload.scheduled_at = new Date(form.scheduledLocal).toISOString();
      }
      const { data } = await api.post('/whatsapp/campaigns', payload);
      toast.success(data.campaign?.status === 'scheduled' ? 'Programada' : 'Iniciada', {
        description: `${data.campaign?.stats?.total || 0} leads`,
      });
      setCreateOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error al crear');
    } finally {
      setCreating(false);
    }
  };

  const retryFailed = async (id) => {
    try {
      const { data } = await api.post(`/whatsapp/campaigns/${id}/retry-failed`);
      toast.success('Reintento creado', { description: `${data.campaign?.stats?.total || 0} leads` });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo reintentar');
    }
  };

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/whatsapp/campaigns/${id}`);
      setDetail(data.campaign);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const act = async (id, action) => {
    try {
      await api.post(`/whatsapp/campaigns/${id}/${action}`);
      toast.success(action === 'pause' ? 'Pausada' : action === 'resume' ? 'Reanudada' : 'Cancelada');
      load();
      if (detail?.id === id) openDetail(id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Error');
    }
  };

  const progressPct = (c) => {
    const total = c.stats?.total || (c.lead_ids?.length || 0) || 1;
    const done = c.cursor || 0;
    return Math.min(100, Math.round((done / total) * 100));
  };

  return (
    <div>
      <PageHeader
        title="Campañas"
        description="WhatsApp y email masivos/programados. Corren en el servidor aunque cierres el CRM."
        actions={(
          <div className="flex gap-2">
            <select value={channel} onChange={(e) => setChannel(e.target.value)} className="input w-auto">
              <option value="">Todos los canales</option>
              <option value="whatsapp">WhatsApp</option>
              <option value="email">Email</option>
            </select>
            <button onClick={() => setCreateOpen(true)} className="btn-primary">
              Nueva campaña
            </button>
            <button onClick={load} className="btn-ghost">
              <RefreshCw className="h-4 w-4" /> Actualizar
            </button>
          </div>
        )}
      />

      {loading ? (
        <LoadingShell />
      ) : items.length === 0 ? (
        <EmptyState
          icon={CalendarClock}
          title="Sin campañas"
          description="Desde Leads: selecciona contactos → WhatsApp o Email masivo → intervalo y programa o inicia ya."
        />
      ) : (
        <div className="space-y-3">
          {items.map((c) => {
            const total = c.stats?.total ?? c.lead_ids?.length ?? 0;
            const sent = c.stats?.sent ?? 0;
            const failed = c.stats?.failed ?? 0;
            const skipped = c.stats?.skipped ?? 0;
            const replied = c.stats?.replied ?? 0;
            const replyRate = sent > 0 ? Math.round((replied / sent) * 100) : 0;
            const pct = progressPct(c);
            return (
              <div key={c.id} className="surface p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-medium truncate">{c.name || 'Campaña'}</h3>
                      <span className="text-[11px] px-2 py-0.5 rounded-full font-medium bg-muted text-muted-foreground">
                        {(c.channel || 'whatsapp') === 'email' ? 'Email' : 'WhatsApp'}
                      </span>
                      <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${STATUS_CLASS[c.status] || 'bg-muted'}`}>
                        {STATUS_LABEL[c.status] || c.status}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Inicio: {formatWhen(c.scheduled_at)} · Intervalo: {formatInterval(c.interval_seconds)} ·{' '}
                      {fmtRelative(c.created_at)}
                    </p>
                    {c.pause_reason && (
                      <p className="text-xs text-[hsl(var(--warning))] mt-1">{c.pause_reason}</p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {(c.status === 'running' || c.status === 'scheduled') && (
                      <button onClick={() => act(c.id, 'pause')} className="btn-ghost h-8 px-2 text-xs">
                        <Pause className="h-3.5 w-3.5" /> Pausar
                      </button>
                    )}
                    {c.status === 'paused' && (
                      <button onClick={() => act(c.id, 'resume')} className="btn-ghost h-8 px-2 text-xs">
                        <Play className="h-3.5 w-3.5" /> Reanudar
                      </button>
                    )}
                    {['running', 'scheduled', 'paused'].includes(c.status) && (
                      <button onClick={() => act(c.id, 'cancel')} className="btn-ghost h-8 px-2 text-xs text-[hsl(var(--danger))]">
                        <XCircle className="h-3.5 w-3.5" /> Cancelar
                      </button>
                    )}
                    {['completed', 'cancelled', 'paused'].includes(c.status) && (
                      <button onClick={() => retryFailed(c.id)} className="btn-ghost h-8 px-2 text-xs">
                        Reintentar fallidos
                      </button>
                    )}
                    <button onClick={() => openDetail(c.id)} className="btn-ghost h-8 px-2 text-xs">
                      Detalle
                    </button>
                    <button
                      type="button"
                      className="btn-ghost h-8 px-2 text-xs"
                      onClick={async () => {
                        try {
                          const tok = localStorage.getItem('zunoid_access');
                          const res = await fetch(`${API_BASE}/whatsapp/campaigns/${c.id}/export`, {
                            headers: tok ? { Authorization: `Bearer ${tok}` } : {},
                          });
                          if (!res.ok) throw new Error('fail');
                          const blob = await res.blob();
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `campaign_${c.id.slice(0, 8)}.csv`;
                          a.click();
                          URL.revokeObjectURL(url);
                          toast.success('CSV descargado');
                        } catch {
                          toast.error('No se pudo exportar');
                        }
                      }}
                    >
                      CSV
                    </button>
                  </div>
                </div>

                <div className="mt-3">
                  <div className="flex justify-between text-xs text-muted-foreground mb-1">
                    <span>
                      Progreso: {c.cursor || 0}/{total} · Enviados {sent} · Fallidos {failed} · Saltados {skipped}
                      {c.stats?.fallback ? ` · Fallback ${c.stats.fallback}` : ''}
                      {(c.channel || 'whatsapp') !== 'email' ? ` · Respuestas ${replied}${sent ? ` (${replyRate}%)` : ''}` : ''}
                    </span>
                    <span>{pct}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-secondary overflow-hidden">
                    <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
                  </div>
                </div>
                {(c.channel === 'email' && c.subject) && (
                  <p className="mt-2 text-xs font-medium truncate">Asunto: {c.subject}</p>
                )}
                {c.body && (
                  <pre className="mt-2 text-xs text-muted-foreground whitespace-pre-wrap line-clamp-2 bg-secondary/40 rounded-lg p-2">
                    {c.body.replace(/<[^>]+>/g, ' ').slice(0, 280)}
                  </pre>
                )}
              </div>
            );
          })}
        </div>
      )}

      {createOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setCreateOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="surface w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto space-y-3">
            <h2 className="font-display text-lg font-semibold">Nueva campaña por audiencia</h2>
            <label className="block text-xs">Nombre<input className="input mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
            <label className="block text-xs">Canal
              <select className="input mt-1" value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })}>
                <option value="whatsapp">WhatsApp</option>
                <option value="email">Email</option>
              </select>
            </label>
            <label className="block text-xs">Lista de leads (cartera)
              <select className="input mt-1" value={form.list_id} onChange={(e) => setForm({ ...form, list_id: e.target.value })}>
                <option value="">— sin lista (filtros abajo) —</option>
                {leadLists.map((lst) => (
                  <option key={lst.id} value={lst.id}>
                    {lst.name}{lst.lead_count != null ? ` (${lst.lead_count})` : ''}
                  </option>
                ))}
              </select>
            </label>
            {!form.list_id && (
              <div className="grid grid-cols-2 gap-2">
                <label className="block text-xs">Etapa
                  <select className="input mt-1" value={form.stage_id} onChange={(e) => setForm({ ...form, stage_id: e.target.value })}>
                    <option value="">Todas</option>
                    {stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </label>
                <label className="block text-xs">Tag
                  <select className="input mt-1" value={form.tag} onChange={(e) => setForm({ ...form, tag: e.target.value })}>
                    <option value="">Todos</option>
                    {tags.map((t) => <option key={t.id || t.name} value={t.name}>{t.name}</option>)}
                  </select>
                </label>
              </div>
            )}
            {form.channel === 'email' && (
              <label className="block text-xs">Asunto<input className="input mt-1" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} /></label>
            )}
            <label className="block text-xs">Mensaje<textarea className="input mt-1 min-h-[100px]" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} /></label>
            <label className="block text-xs">Intervalo (seg)
              <select className="input mt-1" value={form.interval_seconds} onChange={(e) => setForm({ ...form, interval_seconds: Number(e.target.value) })}>
                {[15, 30, 60, 300, 900].map((s) => <option key={s} value={s}>{s}s</option>)}
              </select>
            </label>
            <label className="block text-xs">Cuándo
              <select className="input mt-1" value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })}>
                <option value="now">Ahora</option>
                <option value="schedule">Programar</option>
              </select>
            </label>
            {form.mode === 'schedule' && (
              <input type="datetime-local" className="input" value={form.scheduledLocal} onChange={(e) => setForm({ ...form, scheduledLocal: e.target.value })} />
            )}
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={form.respect_quiet_hours} onChange={(e) => setForm({ ...form, respect_quiet_hours: e.target.checked })} />
              Respetar horario silencioso del tenant
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button className="btn-ghost" onClick={() => setCreateOpen(false)}>Cancelar</button>
              <button className="btn-primary" disabled={creating} onClick={createFromAudience}>{creating ? '…' : 'Crear e iniciar'}</button>
            </div>
          </div>
        </div>
      )}

      {detail && (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={() => setDetail(null)}>
          <div onClick={(e) => e.stopPropagation()} className="surface w-full max-w-2xl p-6 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-display text-lg font-semibold">{detail.name}</h2>
              <button onClick={() => setDetail(null)} className="btn-ghost h-8 w-8 p-0 text-sm">
                ✕
              </button>
            </div>
            <p className="text-xs text-muted-foreground mb-3">
              Estado: {STATUS_LABEL[detail.status] || detail.status} · Intervalo {formatInterval(detail.interval_seconds)} ·{' '}
              {detail.cursor || 0}/{detail.stats?.total || detail.lead_ids?.length || 0}
              {(detail.channel || 'whatsapp') !== 'email' && (
                <> · Respuestas {detail.stats?.replied || 0}
                  {detail.stats?.sent ? ` (${Math.round(((detail.stats.replied || 0) / detail.stats.sent) * 100)}%)` : ''}
                </>
              )}
            </p>
            {(detail.results || []).some((r) => r.replied_at) && (
              <div className="mb-3">
                <h3 className="text-sm font-medium mb-2">Respondieron</h3>
                <div className="space-y-1 max-h-32 overflow-y-auto mb-2">
                  {(detail.results || [])
                    .filter((r) => r.replied_at)
                    .slice()
                    .reverse()
                    .map((r, i) => (
                      <a
                        key={`rep-${r.lead_id}-${i}`}
                        href={`/app/inbox?lead=${r.lead_id}`}
                        className="text-xs flex gap-2 border-b border-border/50 py-1.5 text-primary hover:underline"
                      >
                        <span className="font-mono text-muted-foreground w-8">#{r.index}</span>
                        <span className="truncate flex-1">{r.lead_id}</span>
                        <span className="text-muted-foreground">{formatWhen(r.replied_at)}</span>
                      </a>
                    ))}
                </div>
              </div>
            )}
            <h3 className="text-sm font-medium mb-2">Últimos resultados</h3>
            <div className="space-y-1 max-h-80 overflow-y-auto">
              {(detail.results || []).slice().reverse().map((r, i) => (
                <div key={`${r.lead_id}-${r.index}-${i}`} className="text-xs flex gap-2 border-b border-border/50 py-1.5">
                  <span className="font-mono text-muted-foreground w-8">#{r.index}</span>
                  <span className="font-medium w-16">{r.status}</span>
                  <span className="truncate flex-1">
                    {r.lead_name || r.lead_phone || r.lead_email || r.lead_id}
                  </span>
                  {r.replied_at && <span className="text-[hsl(var(--success))] shrink-0">✓ resp.</span>}
                  {r.reason && <span className="text-[hsl(var(--warning))] truncate max-w-[40%]">{r.reason}</span>}
                </div>
              ))}
              {!(detail.results || []).length && (
                <p className="text-xs text-muted-foreground">Aún no hay envíos registrados.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
